import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { RootState } from "@/app/rootReducer";
import { createCompanyAction } from "../../slices/companiesSlice";
import { useNotification } from "@/context/NotificationContext";
import { createCompanySchema, type CreateCompanyFormData } from "../../schemas/companySchemas";
import { CreateCompanyDetailsSection } from "../CreateCompanyDetailsSection/CreateCompanyDetailsSection";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";

const CreateCompany: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: RootState) => state.companies);
  const { showNotification } = useNotification();
  const [currentTab, setCurrentTab] = useState("Company Details");

  const form = useForm<CreateCompanyFormData>({
    resolver: zodResolver(createCompanySchema),
    defaultValues: {
      companyName: "",
      companyEmail: "",
      websiteUrl: "",
      salesforceId: "",
      intacctId: "",
      marketingEnabled: false,
      hasEaglesAccess: false,
      hasLATAAccess: false,
    },
    mode: "onChange",
  });

  const { handleSubmit, formState } = form;
  const { isSubmitting, isValid } = formState;

  const onSubmit = async (values: CreateCompanyFormData) => {
    await dispatch(
      createCompanyAction(
        {
          companyName: values.companyName,
          websiteUrl: values.websiteUrl || null,
          salesforceId: values.salesforceId || null,
          intacctId: values.intacctId || null,
          companyEmail: values.companyEmail || null,
          hasEaglesAccess: values.hasEaglesAccess,
          hasLATAAccess: values.hasLATAAccess,
          marketingEnabled: values.marketingEnabled,
        },
        (newCompany) => {
          showNotification(
            "Company Created",
            "The company has been created successfully. You can now add trades and software configuration.",
            "success"
          );
          navigate(`/admin/companies/${newCompany.uuid}/edit`);
        }
      )
    );
  };

  const tabs = [
    { name: "Company Details", current: currentTab === "Company Details" },
  ];

  return (
    <MainPageWrapper
      error={error}
      loading={loading}
      title="Create Company"
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Info Alert */}
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              After creating the company, you'll be redirected to the edit page where you can add trade access and configure software settings.
            </AlertDescription>
          </Alert>

          {/* Company Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>Company Details</CardTitle>
              <CardDescription>
                Enter the basic information for the new company
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CreateCompanyDetailsSection form={form} />
            </CardContent>
          </Card>

          {/* Software Configuration Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>Software Configuration</CardTitle>
              <CardDescription>
                Software configuration can be added after creating the company
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  Field service software selection will be available on the edit page after the company is created.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Trade Access Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>Trade Access</CardTitle>
              <CardDescription>
                Trade access can be configured after creating the company
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  Trade selections will be available on the edit page after the company is created.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/companies")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Company"}
            </Button>
          </div>
        </form>
      </Form>
    </MainPageWrapper>
  );
};

export default CreateCompany;